import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, Sparkles } from "lucide-react";

/**
 * Persistent WebSocket subscription for the logged-in DJ. Renders a big
 * animated toast every time a tip lands (live or via public /tip page),
 * so the DJ (and the room reading over their shoulder) sees:
 *
 *    💵 $10 from Sarah — "play something dirty"  🎉
 *
 * Auto-reconnects with exponential backoff. Renders nothing while
 * disconnected or when no tips have landed.
 */
export default function DjTipToast() {
  const { user, token } = useAuth();
  const [current, setCurrent] = useState(null); // {amount_cents, tipper_name, message, id}
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!user || !token) return;
    const base = BACKEND_URL.replace(/^http/, "ws");
    let closed = false;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(`${base}/api/dj/notify?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => { retryRef.current = 0; };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "tip") {
            setCurrent({
              id: Date.now() + Math.random(),
              amount_cents: evt.amount_cents,
              tipper_name: evt.tipper_name || "anonymous",
              message: evt.message || "",
              source: evt.source,
            });
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (closed) return;
        retryRef.current = Math.min(retryRef.current + 1, 5);
        const delay = 800 * (2 ** retryRef.current);
        setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => {
      closed = true;
      try { wsRef.current?.close(); } catch {}
    };
  }, [user, token]);

  // Auto-dismiss after 8s
  useEffect(() => {
    if (!current) return;
    const t = setTimeout(() => setCurrent(null), 8000);
    return () => clearTimeout(t);
  }, [current]);

  if (!current) return null;

  const amount = (current.amount_cents / 100).toFixed(current.amount_cents % 100 ? 2 : 0);

  return (
    <div
      data-testid="dj-tip-toast"
      role="status"
      aria-live="polite"
      className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none w-[min(92vw,540px)]"
    >
      <div
        className="rounded-2xl bg-gradient-to-br from-[#10B981] to-[#00F0FF] p-[2px] shadow-[0_0_60px_rgba(16,185,129,0.35)]"
        style={{ animation: "dj-tip-in 400ms cubic-bezier(0.34,1.56,0.64,1)" }}
      >
        <div className="rounded-2xl bg-[#0B0E14] p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#10B981]/15 border border-[#10B981] flex items-center justify-center flex-shrink-0"
               style={{ animation: "dj-tip-pulse 900ms ease-out infinite" }}>
            <DollarSign className="w-7 h-7 text-[#10B981]" strokeWidth={2.5}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display font-black text-3xl text-white">${amount}</span>
              <span className="text-white/70 text-base">USD from</span>
              <span className="font-black text-lg text-[#00F0FF] truncate" data-testid="dj-tip-toast-name">{current.tipper_name}</span>
              <Sparkles className="w-4 h-4 text-[#10B981]"/>
            </div>
            {current.message ? (
              <div className="text-sm text-[#94A3B8] mt-1 truncate italic" data-testid="dj-tip-toast-message">
                "{current.message}"
              </div>
            ) : (
              <div className="text-[11px] text-[#94A3B8] font-mono uppercase tracking-widest mt-1">
                {current.source === "public_tip" ? "Tip jar · QR scan" : "Live session tip"}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes dj-tip-in {
          0% { transform: translateY(-40px) scale(0.9); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes dj-tip-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}
